import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Fade,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import api from '../api';

// Background images that rotate on refresh
const heroBackgrounds = [
  '/dallas1.png',
  '/dallas2.png',
  '/dallas3.png',
  '/country1.png',
];

const services = [
  {
    icon: LocalGasStationIcon,
    title: 'Fuel Delivery',
    description: 'Ran out of gas? We\'ll bring fuel directly to you on the roadside.',
  },
  {
    icon: BatteryChargingFullIcon,
    title: 'Jump Starts',
    description: 'Dead battery? Our volunteers will jump or charge you back up in no time.',
  },
  {
    icon: TireRepairIcon,
    title: 'Tire Changes',
    description: 'Flat tire? We\'ll help you swap it out safely on the shoulder.',
  },
  {
    icon: SupportAgentIcon,
    title: 'Roadside Support',
    description: 'Stranded? We provide assistance and peace of mind when you need it most.',
  },
];

function Home() {
  const [news, setNews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Random background image on each render
  const heroBackground = useMemo(() => {
    return heroBackgrounds[Math.floor(Math.random() * heroBackgrounds.length)];
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await api.getNews(3);
        setNews(data);
      } catch (err) {
        console.error('Failed to fetch news:', err);
      }
    };
    fetchNews();
    setLoaded(true);
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${heroBackground}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 35, 126, 0.85) 0%, rgba(13, 27, 94, 0.9) 50%, rgba(0, 0, 81, 0.92) 100%)',
            zIndex: 1,
          }}
        />
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(191,54,12,0.3) 0%, transparent 70%)',
            filter: 'blur(60px)',
            zIndex: 2,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(26,35,126,0.5) 0%, transparent 70%)',
            filter: 'blur(80px)',
            zIndex: 2,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
          <Fade in={loaded} timeout={800}>
            <Box sx={{ maxWidth: 700 }}>
              <Typography
                variant="h1"
                sx={{
                  color: '#fff',
                  mb: 3,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                  textShadow: '0 2px 40px rgba(0,0,0,0.3)',
                }}
              >
                Texans Helping Texans,{' '}
                <Box component="span" sx={{ color: 'secondary.main' }}>
                  One Mile at a Time.
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                }}
              >
                Texas Highway Rangers is a volunteer-driven nonprofit providing free roadside 
                assistance across North Texas. No payment. No membership. Just neighbors helping neighbors.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={Link}
                  to="/about"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: '#fff',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                  }}
                >
                  Learn More
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '40%': { transform: 'translateX(-50%) translateY(-10px)' },
              '60%': { transform: 'translateX(-50%) translateY(-5px)' },
            },
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 50,
              border: '2px solid rgba(255,255,255,0.5)',
              borderRadius: 15,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 6,
                height: 10,
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: 3,
                animation: 'scroll 2s infinite',
              },
              '@keyframes scroll': {
                '0%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                '100%': { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
              },
            }}
          />
        </Box>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 12, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
            >
              What We Do
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2 }}>
              Courtesy Roadside Assistance
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Our trained volunteers provide essential services to stranded motorists 
              throughout the Dallas-Fort Worth and Austin area, completely free of charge.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 2,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(26, 35, 126, 0.15)',
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <service.icon sx={{ fontSize: 32, color: '#fff' }} />
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1.5 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #1a237e 0%, #0d1b5e 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
            }}
          >
            2024 Impact
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {[
              { number: '390+', label: 'Motorists Helped' },
              { number: '129', label: 'Hours Helping' },
              { number: 'Dallas', label: 'Coverage Area' },
              { number: 'Austin', label: 'Coverage Area' },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: 'secondary.main',
                      mb: 1,
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ opacity: 0.9, fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* News Section */}
      {news.length > 0 && (
        <Box sx={{ py: 12, bgcolor: '#fafafa' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
              >
                Stay Updated
              </Typography>
              <Typography variant="h2" sx={{ mt: 1 }}>
                Latest News
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {news.map((post) => (
                <Grid item xs={12} md={4} key={post.id}>
                  <Card
                    component={Link}
                    to={`/news/${post.slug}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {post.imageUrl && (
                      <Box
                        sx={{
                          height: 200,
                          backgroundImage: `url(${post.imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 2 }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {post.excerpt}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                component={Link}
                to="/news"
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                View All News
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      {/* CTA Section */}
      <Box sx={{ py: 12, bgcolor: '#fff' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #bf360c 0%, #870000 100%)',
              color: '#fff',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, color: '#fff' }}>
              Support Our Mission
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, opacity: 0.9, maxWidth: 500, mx: 'auto' }}
            >
              Your donation helps keep our volunteers on the road, providing free assistance 
              to stranded motorists across North Texas.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://www.paypal.com/donate/?hosted_button_id=YOUR_BUTTON_ID"
              target="_blank"
              sx={{
                bgcolor: '#fff',
                color: 'secondary.main',
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Donate Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
